// shared/ui/ImagenPendiente.jsx  (presentacional: miniatura de una imagen pendiente de subir; compartida por formularios y detalle)
// Los object URLs de vista previa se crean/revocan aquí (API del navegador, efecto ligado al ciclo de vida del thumbnail).
import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { removeBtnSx, deleteBtnSx, ERROR_BORDER, BRAND_GREEN, veilSx } from './trashBadgeSx';

const thumbSx = size => ({
  width: size,
  height: size,
  borderRadius: 12,
  background: '#eef0f3',
  position: 'relative',
  overflow: 'hidden',
  flexShrink: 0,
});
const imgStyle = { width: '100%', height: '100%', objectFit: 'cover', display: 'block' };

const CHECK_MS = 900; // tiempo que se ve la marca de éxito antes de pasar a "guardada"

const checkBadgeSx = {
  position: 'absolute',
  top: 6,
  right: 6,
  width: 24,
  height: 24,
  borderRadius: '50%',
  background: BRAND_GREEN,
  color: '#fff',
  fontSize: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

// Estados (vienen de application): procesando (optimizando) | queued | uploading | done | error.
// El "×" queda arriba a la derecha (igual que la papelera de las guardadas) salvo en error: ahí esa esquina es del
// reintento (acción principal) y el "×" pasa arriba a la izquierda para poder descartar la imagen fallida.
const ImagenPendiente = ({ item, size = 120, disabled, onRemove, onRetry, onDone }) => {
  const [url, setUrl] = React.useState('');
  React.useEffect(() => {
    const objectUrl = URL.createObjectURL(item.file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [item.file]);

  // 'done': se muestra la marca un instante y luego la imagen pasa a la lista de guardadas (application).
  const { estado, uuid } = item;
  React.useEffect(() => {
    if (estado !== 'done') return undefined;
    const timer = setTimeout(() => onDone(uuid), CHECK_MS);
    return () => clearTimeout(timer);
  }, [estado, uuid, onDone]);

  const procesando = estado === 'procesando';
  const subiendo = estado === 'uploading' || procesando;
  const fallo = estado === 'error';
  const mensaje = item.error || 'No se pudo subir la imagen.';

  return (
    <div
      style={{ ...thumbSx(size), ...(fallo ? { boxSizing: 'border-box', border: `2px solid ${ERROR_BORDER}` } : {}) }}
      title={fallo ? mensaje : procesando ? 'Optimizando imagen' : item.file.name}
      aria-busy={subiendo}>
      {url && <img src={url} alt={item.file.name} style={imgStyle} />}
      {subiendo && (
        <Box sx={veilSx}>
          <CircularProgress size={24} aria-label={procesando ? 'Optimizando imagen' : 'Subiendo imagen'} sx={{ color: BRAND_GREEN }} />
        </Box>
      )}
      {estado === 'done' && (
        <Box sx={checkBadgeSx} role="img" aria-label="Imagen subida">
          <i className="fa fa-check" />
        </Box>
      )}
      {fallo && (
        <Box
          component="button"
          type="button"
          aria-label="Reintentar subida"
          title={mensaje}
          disabled={disabled}
          onClick={() => onRetry(item)}
          sx={deleteBtnSx}>
          <i className="fa fa-redo" />
        </Box>
      )}
      {(estado === 'queued' || fallo) && (
        <Box
          component="button"
          type="button"
          aria-label="Quitar imagen"
          title="Quitar imagen"
          disabled={disabled}
          onClick={() => onRemove(uuid)}
          sx={fallo ? { ...removeBtnSx, right: 'auto', left: 6 } : removeBtnSx}>
          ×
        </Box>
      )}
    </div>
  );
};

export default ImagenPendiente;
